import { z } from "zod"
import { TranslationKey } from "../i18n/translation-key"

/**
 * How a discount is expressed. A "new price" entered by a mechanic is stored
 * as an `AMOUNT` (original − new price), so customers see it exactly like a
 * kr discount.
 */
export enum DiscountKind {
  PERCENT = "PERCENT",
  AMOUNT = "AMOUNT",
}

export const PercentDiscountSchema = z.object({
  kind: z.literal(DiscountKind.PERCENT),
  value: z.number().int().min(0).max(100),
})

/** A kr discount of 0 means nothing, so it is never given. */
export const AmountDiscountSchema = z.object({
  kind: z.literal(DiscountKind.AMOUNT),
  value: z
    .number()
    .int()
    .min(
      1,
      "common.validation.discountAtLeastOneKrona" satisfies TranslationKey,
    ),
})

export const DiscountSchema = z.discriminatedUnion("kind", [
  PercentDiscountSchema,
  AmountDiscountSchema,
])

export type Discount = z.infer<typeof DiscountSchema>

/**
 * Accepts a `Discount` or a bare number. JSON stored before kr discounts
 * existed (addon orders, invoice rows) holds plain percent numbers.
 */
export const LegacyCompatibleDiscountSchema = z.union([
  DiscountSchema,
  z
    .number()
    .int()
    .min(0)
    .max(100)
    .transform((value): Discount => ({ kind: DiscountKind.PERCENT, value })),
])

export const percentDiscount = (value: number): Discount => ({
  kind: DiscountKind.PERCENT,
  value,
})

export const amountDiscount = (value: number): Discount => ({
  kind: DiscountKind.AMOUNT,
  value,
})

/** True when the discount actually lowers a price. */
export const hasDiscount = (
  discount: Discount | null | undefined,
): discount is Discount => discount != null && discount.value > 0

/**
 * Builds a `Discount` from the two sibling database columns (percent and kr).
 * At most one of them is set; a CHECK constraint guarantees it.
 */
export const discountFromColumns = (
  percent: number | null | undefined,
  amount: number | null | undefined,
): Discount | null => {
  if (amount != null) return amountDiscount(amount)
  if (percent != null) return percentDiscount(percent)
  return null
}

/** The sibling database columns (percent and kr) for a `Discount`. */
export const discountToColumns = (
  discount: Discount | null | undefined,
): { percent: number | null; amount: number | null } => {
  if (discount == null) return { percent: null, amount: null }
  if (discount.kind === DiscountKind.AMOUNT) {
    return { percent: null, amount: discount.value }
  }
  return { percent: discount.value, amount: null }
}

export const isSameDiscount = (
  a: Discount | null | undefined,
  b: Discount | null | undefined,
): boolean => {
  if (a == null || b == null) return a == null && b == null
  return a.kind === b.kind && a.value === b.value
}
